import type { User } from '~/constants/auth';

interface KratosFlow {
  id: string;
  ui: {
    action: string;
    method: string;
    nodes: any[];
    messages?: Array<{ id: number; text: string; type: string }>;
  };
}

function extractCsrfToken(flow: KratosFlow): string {
  const nodes = flow?.ui?.nodes;
  if (!Array.isArray(nodes)) return '';
  const csrfNode = nodes.find(
    (node: any) =>
      node.attributes?.name === 'csrf_token' &&
      node.attributes?.type === 'hidden',
  );
  return csrfNode?.attributes?.value ?? '';
}

/**
 * Extrai mensagens de erro de uma resposta do Kratos (settings/recovery/login).
 *
 * Kratos coloca erros em DOIS lugares:
 *  - `ui.messages` — erros gerais do flow (ex.: CSRF inválido)
 *  - `ui.nodes[].messages` — erros por campo (ex.: HIBP, comprimento mínimo,
 *     similaridade com identifier). É AQUI que aparecem as validações de
 *     senha — se ler só `ui.messages`, esses erros somem e o usuário vê
 *     fallback genérico.
 */
export function extractKratosErrorMessages(payload: any): string[] {
  const ui = payload?.ui ?? payload?.data?.ui;
  if (!ui) return [];

  const flowMsgs = (ui.messages ?? []).filter((m: any) => m.type === 'error');
  const nodeMsgs = (ui.nodes ?? [])
    .flatMap((n: any) => n.messages ?? [])
    .filter((m: any) => m.type === 'error');

  return [...flowMsgs, ...nodeMsgs]
    .map((m: any) => m.text as string)
    .filter(Boolean);
}

const authUser = ref<User | null>(null);

export const useAuth = () => {
  const user = computed(() => authUser.value);
  const isAuthenticated = computed(() => !!authUser.value);
  const isAdmin = computed(() => authUser.value?.role === 'admin');

  // === Login Flow (via NestJS) ===

  const initLoginFlow = async (): Promise<KratosFlow> => {
    return await $fetch<KratosFlow>('/api/auth/login/flow', {
      credentials: 'include',
    });
  };

  const login = async (
    flowId: string,
    csrfToken: string,
    cpf: string,
    password: string,
  ) => {
    await $fetch(
      '/api/auth/login',
      {
        method: 'POST',
        credentials: 'include',
        body: {
          flowId,
          csrfToken,
          identifier: cpf,
          password,
        },
      },
    );

    // Após login, buscar dados decriptados do usuário via /auth/me
    const ok = await fetchUser();
    if (!ok) throw new Error('Resposta de login sem sessão válida.');
  };

  // === Session (via NestJS) ===

  const fetchUser = async (): Promise<boolean> => {
    try {
      const user = await $fetch<User>('/api/auth/me', {
        credentials: 'include',
      });
      if (!user) return false;
      authUser.value = user;
      return true;
    } catch {
      authUser.value = null;
      return false;
    }
  };

  const logout = async () => {
    try {
      await $fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Sessão pode já estar expirada
    } finally {
      authUser.value = null;
      await navigateTo('/login');
    }
  };

  // === Recovery Flow (via NestJS) ===

  const initRecoveryFlow = async (): Promise<KratosFlow> => {
    return await $fetch<KratosFlow>('/api/auth/recovery/flow', {
      credentials: 'include',
    });
  };

  const submitRecoveryFlow = async (
    flowId: string,
    csrfToken: string,
    email: string,
  ) => {
    return await $fetch<KratosFlow>('/api/auth/recovery', {
      method: 'POST',
      credentials: 'include',
      body: { flowId, csrfToken, email },
    });
  };

  const submitRecoveryCode = async (
    flowId: string,
    csrfToken: string,
    code: string,
    email?: string,
  ) => {
    return await $fetch<any>('/api/auth/recovery/code', {
      method: 'POST',
      credentials: 'include',
      body: { flowId, csrfToken, code, email: email || '' },
    });
  };

  // === WebAuthn (via NestJS) ===

  const requestWebAuthnChallenge = async (
    flowId: string,
    csrfToken: string,
    identifier?: string,
  ): Promise<KratosFlow> => {
    return await $fetch<KratosFlow>('/api/auth/login/webauthn-challenge', {
      method: 'POST',
      credentials: 'include',
      body: { flowId, csrfToken, identifier },
    });
  };

  const loginWithWebAuthn = async (
    flowId: string,
    formData: Record<string, string>,
  ) => {
    await $fetch(
      '/api/auth/login/webauthn',
      {
        method: 'POST',
        credentials: 'include',
        body: { flowId, ...formData },
      },
    );
    const ok = await fetchUser();
    if (!ok) throw new Error('Resposta de login sem sessão válida.');
  };

  const submitWebAuthnRegistration = async (
    flowId: string,
    formData: Record<string, string>,
  ) => {
    return await $fetch<any>('/api/auth/settings/webauthn/register', {
      method: 'POST',
      credentials: 'include',
      body: { flowId, ...formData },
    });
  };

  const removeWebAuthnCredential = async (
    flowId: string,
    csrfToken: string,
    credentialId: string,
  ) => {
    return await $fetch<any>('/api/auth/settings/webauthn/remove', {
      method: 'POST',
      credentials: 'include',
      body: { flowId, csrfToken, credentialId },
    });
  };

  // === Settings Flow (via NestJS) ===

  const initSettingsFlow = async (): Promise<KratosFlow> => {
    return await $fetch<KratosFlow>('/api/auth/settings/flow', {
      credentials: 'include',
    });
  };

  const submitPasswordChange = async (
    flowId: string,
    csrfToken: string,
    password: string,
  ) => {
    return await $fetch<any>('/api/auth/settings/password', {
      method: 'POST',
      credentials: 'include',
      body: { flowId, csrfToken, password },
    });
  };

  return {
    user,
    isAuthenticated,
    isAdmin,
    initLoginFlow,
    extractCsrfToken,
    login,
    fetchUser,
    logout,
    initRecoveryFlow,
    submitRecoveryFlow,
    submitRecoveryCode,
    loginWithWebAuthn,
    requestWebAuthnChallenge,
    submitWebAuthnRegistration,
    removeWebAuthnCredential,
    initSettingsFlow,
    submitPasswordChange,
  };
};
